// src/api/token-storage.ts
import axios from "axios";
import { API_BASE_URL } from "../env";
import { storageSet, storageRemove } from "../platform/storage";

/* -------------------------------------------------------------------------- */
/*                               SESSION STORAGE                              */
/* -------------------------------------------------------------------------- */

export const ACCESS_TOKEN_KEY = "access_token";
export const REFRESH_TOKEN_KEY = "refresh_token";
export const EXPIRES_AT_KEY = "expires_at";
export const USER_ID_KEY = "user_id";
export const TOKEN_LIFETIME_KEY = "token_lifetime_sec";
export const LAST_ACTIVE_KEY = "last_active_at";

export type StoredSession = {
    access_token: string;
    refresh_token: string;
    expires_at: number; // seconds since epoch
    user_id: string;
};

export const base64UrlToString = (input: string): string => {
    const b64 = input?.replace(/-/g, "+")?.replace(/_/g, "/");
    const pad = b64.length % 4;
    const padded = pad ? b64 + "=".repeat(4 - pad) : b64;

    try {
        return decodeURIComponent(
            atob(padded)
                .split("")
                .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
                .join(""),
        );
    } catch {
        try {
            return atob(padded);
        } catch {
            return "";
        }
    }
};

export const decodeJwtPayload = (token: string): Record<string, any> | null => {
    try {
        const parts = token.split(".");
        if (parts.length < 2) return null;

        const json = base64UrlToString(parts[1]!);
        if (!json) return null;

        const payload = JSON.parse(json);
        return payload && typeof payload === "object" ? payload : null;
    } catch {
        return null;
    }
};

export function extractUserIdFromAccessToken(
    accessToken: string,
): string | null {
    const payload = decodeJwtPayload(accessToken);
    if (!payload) return null;

    const sub = payload.sub;
    if (typeof sub === "string" && sub.trim()) return sub.trim();

    const uid = payload.user_id || payload.userId || payload.id;
    if (typeof uid === "string" && uid.trim()) return uid.trim();

    return null;
}

export function extractExpiresAtFromAccessToken(accessToken: string): number {
    const payload = decodeJwtPayload(accessToken);
    const exp = payload?.exp;
    return typeof exp === "number" && Number.isFinite(exp) ? exp : 0;
}

export function readStoredSession(): StoredSession {
    const access_token = localStorage.getItem(ACCESS_TOKEN_KEY) ?? "";
    const refresh_token = localStorage.getItem(REFRESH_TOKEN_KEY) ?? "";
    const user_id = localStorage.getItem(USER_ID_KEY) ?? "";

    // Prefer explicit stored expires_at, fall back to JWT exp.
    const raw = localStorage.getItem(EXPIRES_AT_KEY) ?? "";
    let expires_at = raw ? Number(raw) : 0;

    if (!expires_at && access_token) {
        expires_at = extractExpiresAtFromAccessToken(access_token);
    }

    return {
        access_token,
        refresh_token,
        expires_at: Number.isFinite(expires_at) ? expires_at : 0,
        user_id,
    };
}

export function writeStoredSession(input: {
    access_token: string;
    refresh_token: string;
    expires_in?: number;
    expires_at?: number;
    user_id?: string;
}) {
    storageSet(ACCESS_TOKEN_KEY, input.access_token);
    storageSet(REFRESH_TOKEN_KEY, input.refresh_token);

    // user_id
    const uid = (typeof input.user_id === "string" && input.user_id.trim()) ||
        extractUserIdFromAccessToken(input.access_token) ||
        "";
    if (uid) storageSet(USER_ID_KEY, uid);

    // expires_at
    let expires_at = 0;

    if (typeof input.expires_at === "number" && input.expires_at > 0) {
        expires_at = input.expires_at;
    } else {
        const fromJwt = extractExpiresAtFromAccessToken(input.access_token);
        if (fromJwt > 0) {
            expires_at = fromJwt;
        } else if (
            typeof input.expires_in === "number" && input.expires_in > 0
        ) {
            const nowSec = Math.floor(Date.now() / 1000);
            expires_at = nowSec + input.expires_in;
        }
    }

    if (expires_at > 0) {
        storageSet(EXPIRES_AT_KEY, String(expires_at));

        // Derive token lifetime from JWT iat↔exp (used for idle timeout)
        const payload = decodeJwtPayload(input.access_token);
        const iat = payload?.iat;
        if (typeof iat === "number" && iat > 0) {
            const lifetime = expires_at - iat;
            if (lifetime > 0) {
                storageSet(TOKEN_LIFETIME_KEY, String(lifetime));
            }
        }
    } else {
        storageRemove(EXPIRES_AT_KEY);
    }

    // Mark user as active on every session write (login, refresh, etc.)
    storageSet(LAST_ACTIVE_KEY, String(Math.floor(Date.now() / 1000)));
}

export function clearStoredSession() {
    storageRemove(ACCESS_TOKEN_KEY);
    storageRemove(REFRESH_TOKEN_KEY);
    storageRemove(EXPIRES_AT_KEY);
    storageRemove(USER_ID_KEY);
    storageRemove(TOKEN_LIFETIME_KEY);
    storageRemove(LAST_ACTIVE_KEY);
}

export function isAccessTokenExpired(expires_at: number): boolean {
    if (!expires_at) return true; // missing expiry = treat as expired (conservative)
    const nowSec = Math.floor(Date.now() / 1000);
    return nowSec >= expires_at;
}

/* -------------------------------------------------------------------------- */
/*                          ACTIVITY / IDLE TIMEOUT                           */
/* -------------------------------------------------------------------------- */

/** Fixed idle timeout: 50 minutes. After this period of inactivity, the user is logged out. */
const IDLE_TIMEOUT_SEC = 50 * 60; // 50 minutes

export function getSessionIdleTimeoutSec(): number {
    return IDLE_TIMEOUT_SEC;
}

/** Update the "last active" timestamp. */
export function touchLastActive(): void {
    storageSet(LAST_ACTIVE_KEY, String(Math.floor(Date.now() / 1000)));
}

/** Returns true when the user has been idle longer than the token lifetime. */
export function isIdleTooLong(): boolean {
    const raw = localStorage.getItem(LAST_ACTIVE_KEY);
    const lastActive = raw ? Number(raw) : 0;
    if (!lastActive) return false; // no data → don't force logout on first visit
    const nowSec = Math.floor(Date.now() / 1000);
    const timeout = getSessionIdleTimeoutSec();
    return (nowSec - lastActive) > timeout;
}

/* -------------------------------------------------------------------------- */
/*                             REFRESH TOKEN FLOW                             */
/* -------------------------------------------------------------------------- */

// Use a bare client (no interceptors) for refresh calls to avoid recursion.
const refreshClient = axios.create({ baseURL: API_BASE_URL });

// Single in-flight refresh lock (prevents parallel refresh storms)
let refreshPromise:
    | Promise<{
        access_token: string;
        refresh_token: string;
        expires_in?: number;
        expires_at?: number;
    }>
    | null = null;

// Cooldown: prevent rapid-fire refresh attempts after a failure.
// When a refresh fails (e.g., "Refresh Token Not Found"), back off for
// REFRESH_COOLDOWN_MS before trying again. Without this, every 401
// triggers an immediate retry → "refresh not found" → clear session loop.
let lastRefreshFailure = 0;
const REFRESH_COOLDOWN_MS = 10_000; // 10 seconds

export async function refreshSession(): Promise<
    | {
        access_token: string;
        refresh_token: string;
        expires_in?: number;
        expires_at?: number;
    }
    | null
> {
    const { refresh_token } = readStoredSession();
    if (!refresh_token) return null;

    // Skip if we failed recently — prevents rapid 401 → refresh → fail loops
    if (lastRefreshFailure && Date.now() - lastRefreshFailure < REFRESH_COOLDOWN_MS) {
        return null;
    }

    if (!refreshPromise) {
        refreshPromise = (async () => {
            try {
                const { data } = await refreshClient.post("/auth/refresh-token", {
                    refresh_token,
                });

                const access_token = (data as any)?.access_token;
                const new_refresh = (data as any)?.refresh_token;
                const expires_in = (data as any)?.expires_in as number | undefined;

                if (!access_token || !new_refresh) {
                    throw new Error("Refresh succeeded but tokens missing.");
                }

                // Reset cooldown on success
                lastRefreshFailure = 0;

                // ✅ exactOptionalPropertyTypes-safe: omit expires_in if undefined
                writeStoredSession({
                    access_token,
                    refresh_token: new_refresh,
                    ...(typeof expires_in === "number" ? { expires_in } : {}),
                });

                return {
                    access_token,
                    refresh_token: new_refresh,
                    ...(typeof expires_in === "number" ? { expires_in } : {}),
                    expires_at: extractExpiresAtFromAccessToken(access_token) || 0,
                };
            } catch (error: any) {
                // If refresh token is invalid (400/401/403/404), clear ONLY the
                // refresh token. The access token may still be valid (Supabase
                // single-use refresh tokens get consumed — "Refresh Token Not
                // Found" doesn't mean the session is dead). Clearing everything
                // destroys a valid access token and forces an unnecessary logout.
                // If the access token IS dead, the next API call will 401 and
                // the interceptor handles logout.
                const status = error?.response?.status;
                if (status === 400 || status === 401 || status === 403 || status === 404) {
                    lastRefreshFailure = Date.now();
                    // Only clear if the stored token is still the one we used.
                    // Another caller may have already rotated it successfully.
                    const currentStored = localStorage.getItem(REFRESH_TOKEN_KEY);
                    if (currentStored === refresh_token) {
                        console.warn('[token-storage] Refresh token invalid, clearing refresh token only');
                        storageRemove(REFRESH_TOKEN_KEY);
                    } else {
                        console.warn('[token-storage] Refresh token already rotated by another caller, skipping removal');
                    }
                } else {
                    console.error('[token-storage] Refresh failed:', error?.message || error);
                    lastRefreshFailure = Date.now();
                }
                throw error;
            }
        })().finally(() => {
            refreshPromise = null;
        });
    }

    try {
        return await refreshPromise;
    } catch {
        return null;
    }
}
